import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import PageContent from "@/components/PageContent";
import { useToast } from "@/components/ui/use-toast";
import { QueryTable } from "@/components/query-table/QueryTable";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { Module, PATH_DICTS_STORE_GROUPS_EDIT } from "@/constants";
import { defaultDictionaryTableFeatures } from "@/containers/dictionaries/defaultDictionaryTableFeatures";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { useStoreGroupDictionary } from "@/modules/storeGroups/hooks/useStoreGroupDictionary";
import { SuccessFetchListResponse } from "@/services/api";
import { ReadStoreGroup, WriteStoreGroup } from "@/types/storeGroup";
import { ReadStoreGroupMember } from "@/types/storeGroupMember";
import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutationResult } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { readStoreGroupMember } from "@/schemas/storeGroupMember";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { StoreGroupForm } from "../types";
import StoreGroupStores from "@/modules/storeGroups/components/StoreGroupStores";
import { StoreGroupSaveButton } from "./StoreGroupSaveButton";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { DataTableQuery } from "@/types/dataTableQuery";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import useQueryData from "@/hooks/useQueryData";
import { useStoreGroupSelection } from "../hooks/useStoreGroupSelection";
import { RowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { useStoreGroup } from "@/modules/storeGroups/hooks/useStoreGroup";
import { useStoreGroupMembers } from "@/modules/storeGroups/hooks/useStoreGroupMembers";
import { useUpdateStoreGroupStores } from "../hooks/useUpdateStoreGroupStores";
import { useServices } from "@/core/services/useServices";

export default function StoreGroupEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = useStoreGroupDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { logger } = useServices();
  const labels = config.getLabels(t);
  const Icon = config.Icon;
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.StoreGroups,
  );
  const { can_update } = useModulePermissions();

  // Fetch single store group
  const readStoreGroupQuery = useStoreGroup(id);

  // Fetch group members
  const options = useCreateDataTableOptions<ReadStoreGroupMember>("stores");
  const queryKey = useDynamicQueryParams<ReadStoreGroup>(
    `storeGroupStores.${id}`,
    options,
  );
  const storesQuery = useStoreGroupMembers(id, options, queryKey);
  const stores = useQueryData<ReadStoreGroupMember>(storesQuery);

  const form = useForm<StoreGroupForm>({
    resolver: zodResolver(config.schema),
    defaultValues: config.defaultValues,
    disabled: !can_update,
  });

  // Update form name and description when readStoreGroupQuery data loads
  useEffect(() => {
    if (readStoreGroupQuery.data) {
      form.setValue("name", readStoreGroupQuery.data?.name, {
        shouldDirty: false,
      });
      form.setValue("description", readStoreGroupQuery.data?.description, {
        shouldDirty: false,
      });
    }
  }, [readStoreGroupQuery.data, form]);

  const {
    store,
    getStoresToAdd,
    getStoresToDelete,
    onSaveSuccess,
    getHasSelectionChanges,
  } = useStoreGroupSelection({
    stores,
    storeGroupId: id,
    isLoaded: storesQuery.isSuccess,
  });

  const rowSelectionContextValue = useMemo(() => ({ store }), [store]);
  const updateStoresMutation = useUpdateStoreGroupStores(id);

  const onSubmit = useCallback(
    async (values: StoreGroupForm) => {
      try {
        // Single mutation that sends the full payload to /stores endpoint
        // which includes name, description, and store changes
        const response = await updateStoresMutation.mutateAsync({
          name: values.name,
          description: values.description,
          stores: {
            add: getStoresToAdd(),
            delete: getStoresToDelete(),
          },
        } as WriteStoreGroup);

        if (!response) {
          toast({
            title: t("storeGroups.errors.noResponse"),
            variant: "destructive",
          });

          throw new Error("No response from updateStoreGroupStores mutation");
        }

        await storesQuery.refetch();
        await updateQueryDataForExistingItem(response);
        const storeGroupId = response.id || id;
        navigate(generatePath(PATH_DICTS_STORE_GROUPS_EDIT, { storeGroupId }));
        toast({ title: labels.success.update });
        onSaveSuccess();
        form.reset(form.getValues());
      } catch (error) {
        logger.captureException(error, {
          context: "StoreGroupEditor.onSubmit",
          storeGroupId: id,
        });
      }
    },
    [
      updateStoresMutation,
      id,
      getStoresToAdd,
      getStoresToDelete,
      storesQuery,
      updateQueryDataForExistingItem,
      navigate,
      labels.success.update,
      toast,
      logger,
      onSaveSuccess,
      form,
    ],
  );

  const storesQueryContextValue = useMemo<
    DataTableQuery<SuccessFetchListResponse<ReadStoreGroupMember>>
  >(
    () => ({
      query: storesQuery,
    }),
    [storesQuery],
  );

  const dictionaryRecordContext = useMemo(
    () => ({
      config,
      query: readStoreGroupQuery,
      // TODO: Remove this temporary cast after dictionary migration by introducing
      // a consistent mutation contract for dictionary context (global refactor).
      mutation: {
        mutate: onSubmit,
        mutateAsync: onSubmit,
        isPending: updateStoresMutation.isPending,
        isError: updateStoresMutation.isError,
        error: updateStoresMutation.error,
      } as unknown as UseMutationResult<
        ReadStoreGroup,
        Error,
        StoreGroupForm,
        unknown
      >,
      form,
      id,
    }),
    [
      config,
      readStoreGroupQuery,
      onSubmit,
      updateStoresMutation.isPending,
      updateStoresMutation.isError,
      updateStoresMutation.error,
      form,
      id,
    ],
  );

  return (
    <DictionaryRecordContextProvider value={dictionaryRecordContext}>
      <DictionaryPageHeader />
      {readStoreGroupQuery.isFetching && <DictionaryLoader />}
      {!readStoreGroupQuery.isFetching && (
        <DictionaryForm>
          <PageContent
            icon={<Icon className="w-6 h-6 mr-3" />}
            footer={
              <StoreGroupSaveButton
                store={store}
                getHasSelectionChanges={getHasSelectionChanges}
              />
            }
            title={labels.title}
          >
            <DataTableQueryContextProvider value={storesQueryContextValue}>
              <RowSelectionContext.Provider value={rowSelectionContextValue}>
                <QueryTable
                  name={`${config.ns}.stores`}
                  schema={readStoreGroupMember}
                  query={storesQuery}
                  options={options}
                  tableFeatures={{
                    ...defaultDictionaryTableFeatures,
                    enableRowSelection: true,
                    enableColumnPinning: false,
                    exportSettings: {
                      moduleName: Module.Stores,
                      queryParams: {
                        store_group_id: id,
                      },
                    },
                    enableSelectAll: true,
                  }}
                >
                  <StoreGroupStores />
                </QueryTable>
              </RowSelectionContext.Provider>
            </DataTableQueryContextProvider>
          </PageContent>
        </DictionaryForm>
      )}
    </DictionaryRecordContextProvider>
  );
}
